import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import QuestionTemplate from './QuestionTemplate';
import usePreventScroll from '../hooks/usePreventScroll';
import './QuestionPackageManagement.css';

const QuestionPackageManagement = () => {
  const [packages, setPackages] = useState([]);
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showPackageForm, setShowPackageForm] = useState(false);
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showQuestionBankModal, setShowQuestionBankModal] = useState(false);
  const [bankQuestions, setBankQuestions] = useState([]);
  const [loadingBank, setLoadingBank] = useState(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(new Set());
  const [packageEnrollments, setPackageEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [grantIdentifier, setGrantIdentifier] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);

  // Prevent background scrolling when any modal is open
  const isAnyModalOpen = showPackageForm || showSectionForm || showQuestionForm || showQuestionBankModal;
  usePreventScroll(isAnyModalOpen);

  useEffect(() => {
    fetchPackages();
  }, []);

  // Debug effect to monitor questions state
  useEffect(() => {
  }, [questions]);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/question-packages/admin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setPackages(data);
      } else {
        console.error('API returned non-array data:', data);
        setPackages([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching packages:', error);
      setPackages([]);
      setLoading(false);
    }
  };

  const fetchSections = async (packageId) => {
    try {
      const response = await fetch(`${API_URL}/api/question-sections/package/${packageId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setSections(data);
      } else {
        console.error('API returned non-array data:', data);
        setSections([]);
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSections([]);
    }
  };

  const fetchPackageEnrollments = async (packageId) => {
    setLoadingEnrollments(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/question-packages/${packageId}/enrollments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setPackageEnrollments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching package enrollments:', error);
      setPackageEnrollments([]);
    } finally {
      setLoadingEnrollments(false);
    }
  };

  const handleGrantPackageAccess = async () => {
    if (!selectedPackage) return;
    const raw = grantIdentifier.trim();
    if (!raw) {
      setMessage('Enter a student email or user ID to grant access.');
      setTimeout(() => setMessage(''), 4000);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setMessage('You must be logged in as an admin.');
      setTimeout(() => setMessage(''), 4000);
      return;
    }
    const body = /^\d+$/.test(raw)
      ? { userId: parseInt(raw, 10) }
      : { identifier: raw };
    setGrantLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/question-packages/${selectedPackage.id}/grant-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setMessage(data.message || 'Access granted.');
        setGrantIdentifier('');
        fetchPackageEnrollments(selectedPackage.id);
        setTimeout(() => setMessage(''), 4000);
      } else {
        setMessage(data.error || `Could not grant access (${response.status})`);
        setTimeout(() => setMessage(''), 6000);
      }
    } catch (error) {
      console.error('Grant access error:', error);
      setMessage(`Error: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevokePackageAccess = async (enrollmentId) => {
    if (!selectedPackage || !enrollmentId) return;
    if (!window.confirm('Remove this student\'s access to this package?')) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch(
        `${API_URL}/api/question-packages/${selectedPackage.id}/enrollments/${enrollmentId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }
      );
      if (response.ok || response.status === 204) {
        setMessage('Access removed.');
        fetchPackageEnrollments(selectedPackage.id);
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json().catch(() => ({}));
        setMessage(data.error || `Could not remove access (${response.status})`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Revoke access error:', error);
      setMessage(`Error: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const fetchQuestions = async (sectionId) => {
    try {
      // Admin panel needs full question data for editing
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/questions/section/${sectionId}`, {
        headers: headers,
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Ensure data is an array and sort by questionOrder
      if (Array.isArray(data)) {
        // Sort questions by questionOrder (with ID as tiebreaker for stable sorting)
        const sortedQuestions = [...data].sort((a, b) => {
          const orderA = a.questionOrder || 999;
          const orderB = b.questionOrder || 999;
          // Primary sort by questionOrder
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          // Secondary sort by ID (creation order) for stable sorting
          return (a.id || 0) - (b.id || 0);
        });
        setQuestions(sortedQuestions);
      } else {
        console.error('API returned non-array data:', data);
        setQuestions([]);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setQuestions([]);
    }
  };

  const handleSavePackage = async (packageData) => {
    try {
      const isEdit = Boolean(editingPackage && editingPackage.id);
      const url = isEdit
        ? `${API_URL}/api/question-packages/${editingPackage.id}`
        : `${API_URL}/api/question-packages`;
      const method = isEdit ? 'PUT' : 'POST';
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Error: You must be logged in as an admin to perform this action');
        setTimeout(() => setMessage(''), 5000);
        return;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(packageData),
      });
      if (response.ok) {
        await response.json();
        setMessage(isEdit ? 'Package updated successfully!' : 'Package created successfully!');
        setShowPackageForm(false);
        setEditingPackage(null);
        fetchPackages();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setMessage(`Error ${isEdit ? 'updating' : 'creating'} package: ${response.status} - ${errorText}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Exception saving package:', error);
      setMessage(`Error ${Boolean(editingPackage) ? 'updating' : 'creating'} package: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const handleSaveSection = async (sectionData) => {
    try {
      const isEdit = Boolean(editingSection && editingSection.id);
      const url = isEdit
        ? `${API_URL}/api/question-sections/${editingSection.id}`
        : `${API_URL}/api/question-sections`;
      const method = isEdit ? 'PUT' : 'POST';
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Error: You must be logged in as an admin to perform this action');
        setTimeout(() => setMessage(''), 5000);
        return;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(sectionData),
      });
      if (response.ok) {
        await response.json();
        setMessage(isEdit ? 'Section updated successfully!' : 'Section created successfully!');
        setShowSectionForm(false);
        setEditingSection(null);
        const packageId = sectionData.questionPackage?.id || sectionData.questionPackageId || selectedPackage?.id;
        if (packageId) fetchSections(packageId);
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setMessage(`Error ${isEdit ? 'updating' : 'creating'} section: ${response.status} - ${errorText}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Exception saving section:', error);
      setMessage(`Error ${Boolean(editingSection) ? 'updating' : 'creating'} section: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const handleSaveQuestion = async (questionData) => {
    try {
      const isEdit = Boolean(editingQuestion && editingQuestion.id);
      const url = isEdit
        ? `${API_URL}/api/questions/${editingQuestion.id}`
        : `${API_URL}/api/questions`;
      const method = isEdit ? 'PUT' : 'POST';
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Error: You must be logged in as an admin to perform this action');
        setTimeout(() => setMessage(''), 5000);
        return;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(questionData),
      });
      if (response.ok) {
        await response.json();
        setMessage(isEdit ? 'Question updated successfully!' : 'Question created successfully!');
        setShowQuestionForm(false);
        setEditingQuestion(null);
        const sectionId = questionData.questionSection?.id || questionData.questionSectionId || selectedSection?.id;
        if (sectionId) fetchQuestions(sectionId);
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setMessage(`Error ${isEdit ? 'updating' : 'creating'} question: ${response.status} - ${errorText}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Exception saving question:', error);
      setMessage(`Error ${Boolean(editingQuestion) ? 'updating' : 'creating'} question: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/question-packages/${packageId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });

        if (response.ok) {
          setMessage('Package deleted successfully!');
          fetchPackages();
          setTimeout(() => setMessage(''), 3000);
        } else {
          setMessage('Error deleting package');
          setTimeout(() => setMessage(''), 3000);
        }
      } catch (error) {
        setMessage('Error deleting package');
        setTimeout(() => setMessage(''), 3000);
      }
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/question-sections/${sectionId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });

        if (response.ok) {
          setMessage('Section deleted successfully!');
          fetchSections(selectedPackage.id);
          setTimeout(() => setMessage(''), 3000);
        } else {
          setMessage('Error deleting section');
          setTimeout(() => setMessage(''), 3000);
        }
      } catch (error) {
        setMessage('Error deleting section');
        setTimeout(() => setMessage(''), 3000);
      }
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (window.confirm('Are you sure you want to remove this question from this section? The question will remain in the question bank.')) {
      try {
        const token = localStorage.getItem('token');
        // Remove from section (not delete from bank)
        const response = await fetch(`${API_URL}/api/questions/${questionId}/sections/${selectedSection.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });

        if (response.ok) {
          setMessage('Question removed from section successfully! (Still available in question bank)');
          fetchQuestions(selectedSection.id);
          setTimeout(() => setMessage(''), 3000);
        } else {
          const errorText = await response.text();
          setMessage(`Error removing question: ${errorText}`);
          setTimeout(() => setMessage(''), 5000);
        }
      } catch (error) {
        setMessage(`Error removing question: ${error.message}`);
        setTimeout(() => setMessage(''), 5000);
      }
    }
  };

  const handleLockQuestion = async (questionId, isLocked) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = isLocked 
        ? `${API_URL}/api/questions/${questionId}/unlock`
        : `${API_URL}/api/questions/${questionId}/lock`;
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        setMessage(isLocked ? 'Question unlocked successfully!' : 'Question locked successfully!');
        fetchQuestions(selectedSection.id);
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setMessage(`Error ${isLocked ? 'unlocking' : 'locking'} question: ${errorText}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      setMessage(`Error ${isLocked ? 'unlocking' : 'locking'} question: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const handleLockSection = async (sectionId, isLocked) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = isLocked 
        ? `${API_URL}/api/question-sections/${sectionId}/unlock`
        : `${API_URL}/api/question-sections/${sectionId}/lock`;
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        setMessage(isLocked ? 'Section unlocked successfully!' : 'Section locked successfully!');
        fetchSections(selectedPackage.id);
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setMessage(`Error ${isLocked ? 'unlocking' : 'locking'} section: ${errorText}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      setMessage(`Error ${isLocked ? 'unlocking' : 'locking'} section: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const fetchQuestionBank = async () => {
    try {
      setLoadingBank(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/questions/bank`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setBankQuestions(Array.isArray(data) ? data : []);
      } else {
        setMessage('Error loading question bank');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error fetching question bank:', error);
      setMessage('Error loading question bank');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoadingBank(false);
    }
  };

  const handleAddQuestionFromBank = async (questionId) => {
    if (!selectedSection) {
      setMessage('Please select a section first');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/questions/${questionId}/sections/${selectedSection.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        setMessage('Question added to section successfully!');
        setShowQuestionBankModal(false);
        fetchQuestions(selectedSection.id);
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setMessage(`Error adding question: ${errorText}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      setMessage(`Error adding question: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const handleAddMultipleQuestionsFromBank = async () => {
    if (!selectedSection) {
      setMessage('Please select a section first');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    if (selectedQuestionIds.size === 0) {
      setMessage('Please select at least one question');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const questionIdsArray = Array.from(selectedQuestionIds);
      let successCount = 0;
      let failCount = 0;

      // Add questions one by one
      for (const questionId of questionIdsArray) {
        try {
          const response = await fetch(`${API_URL}/api/questions/${questionId}/sections/${selectedSection.id}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
        }
      }

      if (successCount > 0) {
        setMessage(`Successfully added ${successCount} question(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
        setSelectedQuestionIds(new Set());
        setShowQuestionBankModal(false);
        fetchQuestions(selectedSection.id);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Failed to add questions. Please try again.');
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      setMessage(`Error adding questions: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const handleToggleQuestionSelection = (questionId) => {
    setSelectedQuestionIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const availableQuestions = bankQuestions.filter(q => !questions.some(existing => existing.id === q.id));
    if (selectedQuestionIds.size === availableQuestions.length) {
      setSelectedQuestionIds(new Set());
    } else {
      setSelectedQuestionIds(new Set(availableQuestions.map(q => q.id)));
    }
  };

  const handleOpenQuestionBank = () => {
    setSelectedQuestionIds(new Set());
    setShowQuestionBankModal(true);
    fetchQuestionBank();
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'BEGINNER': 'green',
      'INTERMEDIATE': 'blue',
      'ADVANCED': 'orange',
      'EXPERT': 'red'
    };
    return colors[difficulty] || 'gray';
  };

  const getDifficultyBadge = (difficulty) => {
    const color = getDifficultyColor(difficulty);
    return (
      <span className={`difficulty-badge ${color}`}>
        {difficulty}
      </span>
    );
  };

  if (loading) {
    return <div className="question-package-management">Loading...</div>;
  }

  return (
    <div className="question-package-management">
      <div className="management-header">
        <h2>Question Package Management</h2>
        <button 
          className="create-btn"
          onClick={() => { setEditingPackage(null); setShowPackageForm(true); }}
        >
          Create New Package
        </button>
      </div>

      {message && (
        <div className="message">
          {message}
        </div>
      )}

      {/* Package Form */}
      {showPackageForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <PackageForm 
              onSubmit={handleSavePackage}
              onCancel={() => setShowPackageForm(false)}
              package={editingPackage}
            />
          </div>
        </div>
      )}

      {/* Section Form */}
      {showSectionForm && selectedPackage && (
        <div className="modal-overlay">
          <div className="modal-content">
            <SectionForm 
              onSubmit={handleSaveSection}
              onCancel={() => setShowSectionForm(false)}
              section={editingSection}
              packageId={selectedPackage.id}
            />
          </div>
        </div>
      )}

      {/* Question Form */}
      {showQuestionForm && selectedSection && (
        <div className="modal-overlay">
          <div className="modal-content">
            <QuestionTemplate 
              onSubmit={handleSaveQuestion}
              onCancel={() => setShowQuestionForm(false)}
              question={editingQuestion}
              sectionId={selectedSection.id}
            />
          </div>
        </div>
      )}

      {/* Packages List */}
      <div className="packages-section">
        <h3>Question Packages ({packages.length})</h3>
        <div className="packages-grid">
          {packages.map(pkg => (
            <div key={pkg.id} className="package-card">
              <div className="package-header">
                <h4>{pkg.name}</h4>
                {getDifficultyBadge(pkg.difficultyLevel)}
              </div>
              <p className="package-description">{pkg.description}</p>
              <div className="package-details">
                <span><strong>Price:</strong> ₹{pkg.price}</span>
                <span><strong>Category:</strong> {pkg.category}</span>
                <span><strong>Questions:</strong> {pkg.totalQuestions || 0}</span>
                <span><strong>Duration:</strong> {pkg.estimatedDurationHours}h</span>
              </div>
              <div className="package-actions">
                <button 
                  className="view-btn"
                  onClick={() => {
                    setSelectedPackage(pkg);
                    fetchSections(pkg.id);
                    fetchPackageEnrollments(pkg.id);
                  }}
                >
                  View Sections
                </button>
                <button 
                  className="edit-btn"
                  onClick={() => {
                    setEditingPackage(pkg);
                    setShowPackageForm(true);
                  }}
                >
                  Edit
                </button>
                <button 
                  className="delete-btn"
                  onClick={() => handleDeletePackage(pkg.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Grant package access (students) */}
      {selectedPackage && (
        <div className="student-access-section">
          <h3>Student access — "{selectedPackage.name}"</h3>
          <p className="student-access-hint">
            Grant the same access as a purchase, without payment. Enter numeric user ID (Users tab), or the student&apos;s email or username.
          </p>
          <div className="grant-access-row">
            <input
              type="text"
              className="grant-access-input"
              placeholder="Email or user ID"
              value={grantIdentifier}
              onChange={(e) => setGrantIdentifier(e.target.value)}
              disabled={grantLoading}
            />
            <button
              type="button"
              className="create-btn"
              onClick={handleGrantPackageAccess}
              disabled={grantLoading}
            >
              {grantLoading ? 'Granting…' : 'Grant access'}
            </button>
          </div>
          <div className="package-enrollments-wrap">
            <h4>Students with access ({loadingEnrollments ? '…' : packageEnrollments.length})</h4>
            {loadingEnrollments ? (
              <p className="enrollments-loading">Loading…</p>
            ) : packageEnrollments.length === 0 ? (
              <p className="enrollments-empty">No enrollments yet. Grant access above or students purchase in the app.</p>
            ) : (
              <table className="package-enrollments-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Enrolled</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {packageEnrollments.map((row) => (
                    <tr key={row.enrollmentId}>
                      <td>{row.user?.username ?? '—'}</td>
                      <td>{row.user?.email ?? '—'}</td>
                      <td>{row.status}</td>
                      <td>
                        {row.enrolledAt
                          ? new Date(row.enrolledAt).toLocaleString()
                          : '—'}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={() => handleRevokePackageAccess(row.enrollmentId)}
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Sections List */}
      {selectedPackage && (
        <div className="sections-section">
          <div className="section-header">
            <h3>Sections for "{selectedPackage.name}" ({sections.length})</h3>
            <button 
              className="create-btn"
              onClick={() => {
                setShowSectionForm(true);
              }}
            >
              Add Section
            </button>
          </div>
          <div className="sections-grid">
            {sections.map(section => (
                  <div key={section.id} className={`section-card ${(section.isLocked === true) ? 'locked' : ''}`}>
                <div className="section-title-row">
                  <h4>{section.title}</h4>
                  {(section.isLocked === true) && <span className="lock-indicator" title="This section is locked">🔒</span>}
                </div>
                <p><strong>Topic:</strong> {section.topic}</p>
                <p className="section-description">{section.description}</p>
                <div className="section-actions">
                  <button 
                    className="view-btn"
                    onClick={() => {
                      setSelectedSection(section);
                      // Always fetch questions separately for performance
                      // (questions are no longer included in section response)
                      fetchQuestions(section.id);
                    }}
                  >
                    View Questions
                  </button>
                  <button 
                    className="edit-btn"
                    onClick={() => {
                      setEditingSection(section);
                      setShowSectionForm(true);
                    }}
                  >
                    Edit
                  </button>
                  <button 
                    className={(section.isLocked === true) ? "unlock-btn" : "lock-btn"}
                    onClick={() => handleLockSection(section.id, section.isLocked === true)}
                    title={(section.isLocked === true) ? "Unlock section for all users" : "Lock section for all users"}
                  >
                    {(section.isLocked === true) ? '🔓 Unlock' : '🔒 Lock'}
                  </button>
                  <button 
                    className="delete-btn"
                    onClick={() => handleDeleteSection(section.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Questions List */}
      {selectedSection && (
        <div className="questions-section">
          {}
          {}
          {}
          <div className="question-header">
            <h3>Questions for "{selectedSection.title}" ({questions.length})</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="create-btn"
                onClick={handleOpenQuestionBank}
                style={{ background: '#28a745' }}
              >
                📚 Add from Bank
              </button>
              <button 
                className="create-btn"
                onClick={() => {
                  setShowQuestionForm(true);
                }}
              >
                ➕ Create New
              </button>
            </div>
          </div>
          <div className="questions-grid">
            {}
            {}
            {questions.length === 0 ? (
              <div className="no-questions">
                <p>No questions found for this section. Click "Add Question" to create one.</p>
              </div>
            ) : (
              questions.map(question => {
                return (
                  <div key={question.id} className={`question-card ${(question.isLocked === true) ? 'locked' : ''}`}>
                    <div className="question-header">
                      <div className="question-title-row">
                        <h4>{question.title || 'No Title'}</h4>
                        {(question.isLocked === true) && <span className="lock-indicator" title="This question is locked">🔒</span>}
                      </div>
                      {getDifficultyBadge(question.difficultyLevel || 'EASY')}
                    </div>
                    <p className="question-description">{question.description || 'No description'}</p>
                    <div className="question-details">
                      <span><strong>Order:</strong> {question.questionOrder || 1}</span>
                      <span><strong>Time Limit:</strong> {question.timeLimitSeconds || 1000}s</span>
                      <span><strong>Memory:</strong> {question.memoryLimitMB || 256}MB</span>
                    </div>
                    <div className="question-actions">
                      <button 
                        className="edit-btn"
                        onClick={() => {
                          setEditingQuestion(question);
                          setShowQuestionForm(true);
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        className={(question.isLocked === true) ? "unlock-btn" : "lock-btn"}
                        onClick={() => handleLockQuestion(question.id, question.isLocked === true)}
                        title={(question.isLocked === true) ? "Unlock question for all users" : "Lock question for all users"}
                      >
                        {(question.isLocked === true) ? '🔓 Unlock' : '🔒 Lock'}
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDeleteQuestion(question.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Question Bank Modal */}
      {showQuestionBankModal && selectedSection && (
        <div className="modal-overlay" onClick={() => setShowQuestionBankModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', maxHeight: '85vh', width: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>📚 Add Questions from Bank to "{selectedSection.title}"</h3>
              <button 
                onClick={() => setShowQuestionBankModal(false)}
                style={{ background: '#dc3545', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
              >
                ✕ Close
              </button>
            </div>
            
            {loadingBank ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading questions...</div>
            ) : bankQuestions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <p>No questions available in the bank.</p>
                <p>Create questions in the Question Bank tab first.</p>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
                  <div>
                    <button
                      onClick={handleSelectAll}
                      style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer', marginRight: '10px' }}
                    >
                      {selectedQuestionIds.size > 0 ? 'Deselect All' : 'Select All'}
                    </button>
                    <span style={{ color: '#666', fontSize: '14px' }}>
                      {selectedQuestionIds.size} question(s) selected
                    </span>
                  </div>
                  <button
                    onClick={handleAddMultipleQuestionsFromBank}
                    disabled={selectedQuestionIds.size === 0}
                    style={{
                      padding: '8px 20px',
                      border: 'none',
                      borderRadius: '4px',
                      background: selectedQuestionIds.size > 0 ? '#28a745' : '#ccc',
                      color: 'white',
                      cursor: selectedQuestionIds.size > 0 ? 'pointer' : 'not-allowed',
                      fontWeight: '600'
                    }}
                  >
                    ➕ Add Selected ({selectedQuestionIds.size})
                  </button>
                </div>
                <div style={{ maxHeight: '55vh', overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '6px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                    <thead style={{ background: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr>
                        <th style={{ padding: '12px', textAlign: 'left', width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={bankQuestions.filter(q => !questions.some(existing => existing.id === q.id)).length > 0 && 
                                     selectedQuestionIds.size === bankQuestions.filter(q => !questions.some(existing => existing.id === q.id)).length}
                            onChange={handleSelectAll}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Title</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Difficulty</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bankQuestions.map(question => {
                        const isAlreadyAdded = questions.some(q => q.id === question.id);
                        const isSelected = selectedQuestionIds.has(question.id);
                        
                        return (
                          <tr key={question.id} style={{ 
                            opacity: isAlreadyAdded ? 0.6 : 1,
                            background: isSelected ? '#e7f3ff' : 'white'
                          }}>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {!isAlreadyAdded && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleToggleQuestionSelection(question.id)}
                                  style={{ cursor: 'pointer' }}
                                />
                              )}
                            </td>
                            <td style={{ padding: '12px', fontWeight: '600', color: '#6c757d' }}>{question.id}</td>
                            <td style={{ padding: '12px' }}>
                              <span style={{ fontWeight: '500', color: '#333' }}>{question.title || 'No Title'}</span>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {getDifficultyBadge(question.difficultyLevel || 'EASY')}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {isAlreadyAdded ? (
                                <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓ Added</span>
                              ) : (
                                <span style={{ color: '#6c757d' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {!isAlreadyAdded && (
                                <button
                                  onClick={() => handleAddQuestionFromBank(question.id)}
                                  style={{
                                    padding: '4px 12px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    background: '#28a745',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  Add
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Simple Package Form Component
const PackageForm = ({ onSubmit, onCancel, package: pkg = null }) => {
  const [formData, setFormData] = useState({
    name: pkg?.name || '',
    description: pkg?.description || '',
    price: pkg?.price || '',
    category: pkg?.category || '',
    difficultyLevel: pkg?.difficultyLevel || 'BEGINNER',
    totalQuestions: pkg?.totalQuestions || 0,
    estimatedDurationHours: pkg?.estimatedDurationHours || 1,
    isActive: pkg?.isActive !== false,
    // Discount fields
    originalPrice: pkg?.originalPrice || '',
    isOnSale: pkg?.isOnSale || false,
    discountPercentage: pkg?.discountPercentage || '',
    discountStartDate: pkg?.discountStartDate ? new Date(pkg.discountStartDate).toISOString().slice(0, 16) : '',
    discountEndDate: pkg?.discountEndDate ? new Date(pkg.discountEndDate).toISOString().slice(0, 16) : '',
    offerDescription: pkg?.offerDescription || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convert price to number and ensure required fields are properly formatted
    const submitData = {
      ...formData,
      price: parseFloat(formData.price) || 0,
      totalQuestions: parseInt(formData.totalQuestions) || 0,
      estimatedDurationHours: parseInt(formData.estimatedDurationHours) || 1,
      // Discount fields
      originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice) : null,
      isOnSale: formData.isOnSale || false,
      discountPercentage: formData.discountPercentage ? parseFloat(formData.discountPercentage) : null,
      discountStartDate: formData.discountStartDate ? new Date(formData.discountStartDate).toISOString() : null,
      discountEndDate: formData.discountEndDate ? new Date(formData.discountEndDate).toISOString() : null,
      offerDescription: formData.offerDescription || null
    };
    onSubmit(submitData);
  };

  return (
    <div className="package-form">
      <h3>{pkg ? 'Edit Package' : 'Create New Package'}</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Package Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
          />
        </div>
        <div className="form-group">
          <label>Description *</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            required
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Price *</label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Category *</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Difficulty Level</label>
            <select
              value={formData.difficultyLevel}
              onChange={(e) => setFormData({...formData, difficultyLevel: e.target.value})}
            >
              <option value="BEGINNER">Beginner</option>
              <option value="INTERMEDIATE">Intermediate</option>
              <option value="ADVANCED">Advanced</option>
              <option value="EXPERT">Expert</option>
            </select>
          </div>
          <div className="form-group">
            <label>Estimated Duration (hours)</label>
            <input
              type="number"
              value={formData.estimatedDurationHours}
              onChange={(e) => setFormData({...formData, estimatedDurationHours: e.target.value})}
            />
          </div>
        </div>
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
            />
            Active Package
          </label>
        </div>
        
        {/* Discount/Offer Section */}
        <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #333' }}>
          <h4 style={{ color: '#fff', marginBottom: '15px' }}>💰 Discount & Offers</h4>
          
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isOnSale}
                onChange={(e) => setFormData({...formData, isOnSale: e.target.checked})}
              />
              Enable Discount/Offer
            </label>
          </div>
          
          {formData.isOnSale && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Original Price (before discount)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.originalPrice}
                    onChange={(e) => setFormData({...formData, originalPrice: e.target.value})}
                    placeholder="e.g., 1000"
                  />
                  <small style={{ color: '#999' }}>Leave empty if current price is original</small>
                </div>
                <div className="form-group">
                  <label>Discount Percentage (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.discountPercentage}
                    onChange={(e) => setFormData({...formData, discountPercentage: e.target.value})}
                    placeholder="e.g., 25 for 25% off"
                  />
                </div>
              </div>
              
              {formData.originalPrice && formData.discountPercentage && (
                <div style={{
                  padding: '15px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '8px',
                  marginTop: '10px',
                  marginBottom: '15px',
                  color: 'white'
                }}>
                  <div style={{fontSize: '14px', marginBottom: '8px', opacity: 0.9}}>💰 Calculated Discounted Price:</div>
                  <div style={{fontSize: '24px', fontWeight: 'bold'}}>
                    ₹{(
                      parseFloat(formData.originalPrice) * 
                      (1 - parseFloat(formData.discountPercentage) / 100)
                    ).toFixed(2)}
                  </div>
                  <div style={{fontSize: '12px', marginTop: '5px', opacity: 0.8}}>
                    Original: ₹{parseFloat(formData.originalPrice).toFixed(2)} - 
                    {parseFloat(formData.discountPercentage)}% = 
                    ₹{(
                      parseFloat(formData.originalPrice) * 
                      (1 - parseFloat(formData.discountPercentage) / 100)
                    ).toFixed(2)}
                  </div>
                </div>
              )}
              
              <div className="form-row">
                <div className="form-group">
                  <label>Discount Start Date</label>
                  <input
                    type="datetime-local"
                    value={formData.discountStartDate}
                    onChange={(e) => setFormData({...formData, discountStartDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Discount End Date</label>
                  <input
                    type="datetime-local"
                    value={formData.discountEndDate}
                    onChange={(e) => setFormData({...formData, discountEndDate: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Offer Description</label>
                <input
                  type="text"
                  value={formData.offerDescription}
                  onChange={(e) => setFormData({...formData, offerDescription: e.target.value})}
                  placeholder="e.g., Limited Time Offer, Flash Sale, Early Bird"
                />
              </div>
            </>
          )}
        </div>
        
        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            {pkg ? 'Update Package' : 'Create Package'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Simple Section Form Component
const SectionForm = ({ onSubmit, onCancel, section = null, packageId }) => {
  const [formData, setFormData] = useState({
    title: section?.title || '',
    topic: section?.topic || '',
    description: section?.description || '',
    sectionOrder: section?.sectionOrder || 1,
    questionPackageId: packageId
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Create the proper structure for the API
    const sectionData = {
      ...formData,
      questionPackage: { id: packageId }
    };
    onSubmit(sectionData);
  };

  return (
    <div className="section-form">
      <h3>{section ? 'Edit Section' : 'Create New Section'}</h3>
      <p>Package ID: {packageId}</p>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Section Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            required
          />
        </div>
        <div className="form-group">
          <label>Topic *</label>
          <input
            type="text"
            value={formData.topic}
            onChange={(e) => setFormData({...formData, topic: e.target.value})}
            required
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
          />
        </div>
        <div className="form-group">
          <label>Section Order</label>
          <input
            type="number"
            value={formData.sectionOrder}
            onChange={(e) => setFormData({...formData, sectionOrder: parseInt(e.target.value)})}
          />
        </div>
        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            {section ? 'Update Section' : 'Create Section'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuestionPackageManagement; 
